import { useQuery } from '@tanstack/react-query'
import { Clock, Pencil, Plus, Trash2 } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EmptyState } from '@/components/feedback/States'
import { DataTable, type Column } from '@/components/tables/DataTable'
import { Button, IconButton } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { PageHeader } from '@/components/ui/PageHeader'
import { classesQuery, shiftsQuery, useDeleteShift } from '@/features/academic/api'
import { DeleteDialog } from '@/features/academic/components/DeleteDialog'
import { ShiftDialog } from '@/features/academic/components/ShiftDialog'
import type { Shift } from '@/features/academic/types'
import { Can } from '@/features/auth/guards'
import { useAuth } from '@/features/auth/useAuth'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { tAny } from '@/i18n'

function minutes(time: string): number {
  const [h = 0, m = 0] = time.split(':').map(Number)
  return h * 60 + m
}

function duration(s: Shift): string | null {
  if (!s.startTime || !s.endTime) return null
  const total = minutes(s.endTime) - minutes(s.startTime)
  if (total <= 0) return null
  const h = Math.floor(total / 60)
  const m = total % 60
  return [h ? `${h} h` : '', m ? `${m} min` : ''].filter(Boolean).join(' ')
}

const timeRange = (s: Shift) =>
  s.startTime || s.endTime ? `${s.startTime ?? '…'} – ${s.endTime ?? '…'}` : tAny('No set hours')

export function ShiftsPage() {
  const { t } = useTranslation('academic')
  useDocumentTitle('Shifts')
  const shifts = useQuery(shiftsQuery())
  const classes = useQuery(classesQuery())
  const remove = useDeleteShift()
  const { can } = useAuth()
  const canManage = can('academic.manage')
  const [editing, setEditing] = useState<Shift | 'new' | null>(null)
  const [deleting, setDeleting] = useState<Shift | null>(null)

  const usage = useMemo(() => {
    const counts = new Map<number, number>()
    for (const c of classes.data ?? [])
      for (const s of c.sections) if (s.shift) counts.set(s.shift.id, (counts.get(s.shift.id) ?? 0) + 1)
    return counts
  }, [classes.data])

  const usedBy = (s: Shift) => {
    const n = usage.get(s.id) ?? 0
    return n === 0 ? t('Not used') : t('{{count}} sections', { count: n })
  }

  const actions = (s: Shift) => (
    <div className="flex justify-end gap-1">
      <IconButton
        label={t('Edit {{name}}', { name: s.name })}
        icon={<Pencil />}
        size="icon-sm"
        onClick={() => setEditing(s)}
      />
      <IconButton
        label={t('Delete {{name}}', { name: s.name })}
        icon={<Trash2 />}
        size="icon-sm"
        className="hover:text-danger-600"
        onClick={() => setDeleting(s)}
      />
    </div>
  )

  const columns: Column<Shift>[] = [
    {
      key: 'name',
      header: t('Shift'),
      cell: (s) => (
        <span className="flex items-center gap-3">
          <span
            className="grid size-8 place-items-center rounded-control bg-brand-50 text-brand-600 [&_svg]:size-4"
            aria-hidden="true"
          >
            <Clock />
          </span>
          <span className="font-medium text-ink">{s.name}</span>
        </span>
      ),
    },
    { key: 'hours', header: t('Hours'), cell: (s) => <span className="tabular">{timeRange(s)}</span> },
    {
      key: 'duration',
      header: t('Duration'),
      hideBelow: 'lg',
      cell: (s) => duration(s) ?? <span className="text-subtle">—</span>,
    },
    {
      key: 'usage',
      header: t('Used by'),
      hideBelow: 'lg',
      cell: (s) => <span className="text-muted">{usedBy(s)}</span>,
    },
    ...(canManage
      ? [
          {
            key: 'actions',
            header: <span className="sr-only">{t('Actions')}</span>,
            align: 'right' as const,
            cell: actions,
          },
        ]
      : []),
  ]

  return (
    <>
      <PageHeader
        title={t('Shifts')}
        description={t('School sessions that sections are timetabled into.')}
        breadcrumbs={[{ label: t('Academic') }, { label: t('Shifts') }]}
        homeHref="/admin"
        actions={
          <Can permission="academic.manage">
            <Button leftIcon={<Plus />} onClick={() => setEditing('new')}>
              {t('New shift')}
            </Button>
          </Can>
        }
      />
      <Card className="overflow-hidden">
        <DataTable
          caption={t('Shifts')}
          columns={columns}
          rows={shifts.data}
          getRowId={(s) => s.id}
          loading={shifts.isPending}
          error={shifts.error}
          onRetry={() => void shifts.refetch()}
          skeletonRows={3}
          mobileCard={(s) => (
            <div className="flex items-center gap-3">
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-ink">{s.name}</p>
                <p className="text-xs text-muted tabular">
                  {timeRange(s)} · {usedBy(s)}
                </p>
              </div>
              {canManage ? actions(s) : null}
            </div>
          )}
          empty={
            <EmptyState
              icon={<Clock />}
              title={t('No shifts yet')}
              description={t('Add a shift if your school runs separate sessions.')}
              compact
            />
          }
        />
      </Card>

      <ShiftDialog
        open={editing !== null}
        onOpenChange={(open) => (open ? undefined : setEditing(null))}
        shift={editing === 'new' ? null : editing}
      />
      <DeleteDialog
        item={deleting}
        onClose={() => setDeleting(null)}
        title={(s) => t('Delete {{name}}?', { name: s.name })}
        description={(s) => {
          const n = usage.get(s.id) ?? 0
          return n > 0
            ? t('{{count}} sections use this shift and will be left without one.', { count: n })
            : t('This cannot be undone.')
        }}
        onDelete={(s) => remove.mutateAsync(s.id)}
        successMessage={(s) => t('{{name}} deleted', { name: s.name })}
      />
    </>
  )
}
